import { handleSignedInFromNative, handleSignedOutFromNative, handleTokenRefreshedFromNative } from './nativeAuth'
import { logger } from '@/utils/logger'
import { router } from '@/App'

/**
 * 미션 페이지 열기 핸들러
 * Native에서 SSE mission 이벤트 수신 시 호출
 */
function handleOpenMissionPage(data: { missionJson: any; consultationId: number }) {
  const { missionJson, consultationId } = data

  logger.info(`📝 미션 페이지로 이동 - 상담 ID: ${consultationId}`)
  logger.debug('미션 데이터:', missionJson)

  try {
    // sessionStorage에 미션 데이터 저장
    sessionStorage.setItem('NATIVE_MISSION_DATA', JSON.stringify({
      missionJson,
      consultationId,
      fromNativeChat: true
    }))

    logger.success('✅ 미션 데이터 sessionStorage 저장 완료')

    // ✅ React Router의 navigate 사용 (SPA 방식 - 페이지 새로고침 없음)
    router.navigate('/mission/create/new')

    logger.success('✅ 미션 생성 페이지로 이동 (SPA 방식)')
  } catch (error) {
    logger.error('❌ 미션 페이지 이동 실패:', error)
  }
}

/**
 * 브릿지가 준비될 때까지 대기하는 함수
 * @param timeout 최대 대기 시간 (ms)
 * @param checkInterval 체크 간격 (ms)
 */
async function waitForBridge(timeout = 3000, checkInterval = 100): Promise<boolean> {
  const startTime = Date.now()

  while (Date.now() - startTime < timeout) {
    if (window.ReactNativeBridge) {
      logger.info('✅ ReactNativeBridge 준비 완료!')
      return true
    }

    // checkInterval만큼 대기
    await new Promise(resolve => setTimeout(resolve, checkInterval))
  }

  logger.warn(`⏱️ ReactNativeBridge 대기 시간 초과 (${timeout}ms)`)
  return false
}

/**
 * 네이티브 브릿지 메시지 리스너 초기화
 * App.tsx 또는 main.tsx에서 앱 시작 시 한 번 호출
 */
export async function initializeNativeBridgeListeners() {
  logger.info('🔗 브릿지 리스너 초기화 시작')

  // 브릿지가 준비될 때까지 대기
  const bridgeReady = await waitForBridge()

  if (!bridgeReady || !window.ReactNativeBridge) {
    logger.warn('❌ WebView 환경이 아님 - 리스너 건너뜀')
    logger.info('window.ReactNativeWebView:', !!window.ReactNativeWebView)
    return
  }

  logger.success('✅ ReactNativeBridge 감지됨')

  // 브릿지 메시지 수신
  window.ReactNativeBridge.onMessage((message: any) => {
    logger.info('📩 브릿지 메시지 수신:', message.type)
    logger.debug('메시지 데이터:', message.data)

    switch (message.type) {
      case 'AUTH:SIGNED_IN':
        logger.info('🔐 로그인 토큰 수신')
        handleSignedInFromNative(message.data.accessToken, message.data.userInfo)
        break

      case 'AUTH:SIGNED_OUT':
        logger.info('👋 로그아웃 알림 수신')
        handleSignedOutFromNative(message.data.reason)
        break

      case 'AUTH:TOKEN_REFRESHED':
        logger.info('🔄 토큰 갱신 알림 수신')
        handleTokenRefreshedFromNative(message.data.accessToken)
        break

      case 'CHAT:OPEN_MISSION_PAGE':
        logger.info('📝 미션 페이지 열기 요청 수신')
        handleOpenMissionPage(message.data)
        break

      default:
        logger.debug('📭 알 수 없는 메시지 타입:', message.type)
        break
    }
  })
  logger.success('✅ 브릿지 리스너 등록 완료')

  // ✅ BRIDGE_READY 메시지 전송 (자동 로그인 트리거)
  setTimeout(() => {
    logger.info('📤 BRIDGE_READY 메시지 전송 중...')
    logger.info('🔄 페이지 로드 타임스탬프:', Date.now())
    try {
      if (window.ReactNativeBridge) {
        window.ReactNativeBridge.sendMessage('bridgeReady', {
          ready: true,
          timestamp: Date.now(),
          url: window.location.href
        })
        logger.success('✅ BRIDGE_READY 전송 완료 - RN이 자동 로그인 시작')
      } else {
        logger.error('❌ ReactNativeBridge가 사라짐')
      }
    } catch (error) {
      logger.error('❌ BRIDGE_READY 전송 실패:', error)
    }
  }, 100) // 리스너 등록 후 최소 대기 시간 (100ms로 단축)
}
